"use client"
import type { TransactionIdentifier } from "@opensea/graphql"
import { EmptyState, Spinner, useTheme } from "@opensea/ui-kit"
import { generateUUID } from "@opensea/utils"
import { Suspense, useEffect, useState } from "react"
import { useClient } from "urql"
import { useTranslations } from "use-intl"
import {
  MoonPayNftCheckoutWidget,
  MoonPayProvider,
} from "../../../lib/moonpay/components"
import {
  MOONPAY_NFT_CLIENT_API_KEY,
  MOONPAY_THEME_ID,
} from "../../../lib/moonpay/constants"
import { fetchMoonPayTransaction } from "../../../lib/moonpay/fetchMoonPayTransaction"
import { fetchMoonPayCheckoutUrlSignature } from "../../../lib/moonpay/fetchMoonPayUrlSignature"
import { useIsMoonPayAllowed } from "../../../lib/moonpay/useIsMoonPayAllowed"
import { useAddress } from "../../../polychain/hooks"
import type { Item } from "../../../types"

const POLL_INTERVAL = 3000

type Props = {
  item: Item
  listingId: string
  onTransactionCreated?: (transactionIdentifier: TransactionIdentifier) => void
}

export const MoonPayCheckoutWidget = ({
  item,
  listingId,
  onTransactionCreated,
}: Props) => {
  const { isBuyWithCardAllowed, isLoading } = useIsMoonPayAllowed()
  const { theme } = useTheme()
  const [hasError, setHasError] = useState(false)
  const address = useAddress()
  const t = useTranslations("wallet.MoonPayCheckoutWidget")
  const client = useClient()

  const [moonpayTxId] = useState(() =>
    generateUUID().replace(/-/g, "").slice(0, 12),
  )

  useEffect(() => {
    let pollingCancelled = false

    const pollMoonpayTx = async () => {
      if (!pollingCancelled) {
        const moonpayTx = await fetchMoonPayTransaction(moonpayTxId).catch(
          // Ignore errors
          () => undefined,
        )
        if (!moonpayTx?.nftTransaction?.transactionHash) {
          setTimeout(pollMoonpayTx, POLL_INTERVAL)
          return
        }
        onTransactionCreated?.({
          chain: item.chain.identifier,
          transactionHash: moonpayTx.nftTransaction.transactionHash,
        })
      }
    }

    pollMoonpayTx()

    return () => {
      pollingCancelled = true
    }
  }, [moonpayTxId, onTransactionCreated, item.chain])

  if (!(isBuyWithCardAllowed || isLoading)) {
    return <EmptyState title={t("notSupported")} variant="no-data" />
  }

  if (hasError) {
    return <EmptyState variant="no-data" />
  }

  return (
    <Suspense fallback={<Spinner size="lg" />}>
      <MoonPayProvider apiKey={MOONPAY_NFT_CLIENT_API_KEY}>
        <MoonPayNftCheckoutWidget
          contractAddress={item.contractAddress}
          externalTransactionId={moonpayTxId}
          listingId={listingId}
          onUrlSignatureRequested={async url => {
            const result = await fetchMoonPayCheckoutUrlSignature(url, client)
            if (!result) {
              setHasError(true)
            }
            return result
          }}
          sellType="Secondary"
          style={{
            height: "100%",
            margin: 0,
            width: "100%",
            border: "1px solid rgb(var(--color-border-1))",
          }}
          theme={theme}
          themeId={MOONPAY_THEME_ID}
          tokenId={item.tokenId}
          variant="embedded"
          walletAddress={address}
        />
      </MoonPayProvider>
    </Suspense>
  )
}
